import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Select,
  useToast,
} from '@chakra-ui/react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CareHomeDiaper } from '../../interfaces/Diaper/IDiaper.ts';
import { ICreateResidentDiaperResponse } from '../../interfaces/Diaper/ICreateResidentDiaperResponse.ts';
import DiaperService from '../../services/DiaperService.ts';
import ResidentHeadline from './ResidentHeadline';
import AleaCheckbox from '../../components/AleaCheckbox/AleaCheckbox.tsx';

interface IncontinenceManagementTabProps {
  diapers: CareHomeDiaper[];
  residentName: string;
  residentId: string;
  diaperUsage: ICreateResidentDiaperResponse[];
  showValidation?: boolean;
  onSaveSuccess?: () => void;
  onSaveError?: (error: any) => void;
  initialHasCatheter?: boolean;
}

interface IncontinenceType {
  type: string;
  day: number;
  night: number;
}

interface IncontinenceManagementFormValues {
  incontinenceTypes: IncontinenceType[];
  has_catheter?: boolean;
}

const IncontinenceManagementTab: React.FC<IncontinenceManagementTabProps> = ({
  diapers,
  residentName,
  residentId,
  diaperUsage: initialDiaperUsage,
  showValidation = false,
  onSaveSuccess,
  onSaveError,
  initialHasCatheter,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    register,
    watch,
    getValues,
    setValue,
    formState: {},
  } = useFormContext<IncontinenceManagementFormValues>();
  const incontinenceTypes = watch('incontinenceTypes', []);
  const watchedHasCatheter = useWatch({ name: 'has_catheter' });
  const initializedRef = React.useRef(false);
  const catheterInitializedRef = React.useRef(false);

  /**
   * Initialize form values based on existing diaper usage
   */
  useEffect(() => {
    if (initializedRef.current) return;
    if (diapers.length === 0) return;

    // If there are already non-zero values, do not overwrite user input
    const existing = getValues('incontinenceTypes') || [];
    const hasUserInput = existing.some((v: any) => (v?.day || 0) > 0 || (v?.night || 0) > 0);
    if (hasUserInput) {
      initializedRef.current = true;
      return;
    }

    const initialTypes = diapers.map((diaper) => {
      const dayUsage = initialDiaperUsage.find(
        (usage) => usage.diaper_id === diaper.diaper_id && usage.usage === 'Day'
      );
      const nightUsage = initialDiaperUsage.find(
        (usage) => usage.diaper_id === diaper.diaper_id && usage.usage === 'Night'
      );

      return {
        type: diaper.diaper.name,
        day: dayUsage?.usage_amount || 0,
        night: nightUsage?.usage_amount || 0,
      };
    });

    setValue('incontinenceTypes', initialTypes, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });

    setTimeout(() => {
      setValue('incontinenceTypes', initialTypes, {
        shouldValidate: true
      });
    }, 0);

    initializedRef.current = true;
  }, [diapers, initialDiaperUsage, getValues, setValue]);

  /**
   * Initialize catheter checkbox from parent (GET result) once
   */
  useEffect(() => {
    if (catheterInitializedRef.current) return;
    if (typeof initialHasCatheter === 'undefined') return;
    setValue('has_catheter', Boolean(initialHasCatheter), {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    });
    catheterInitializedRef.current = true;
  }, [initialHasCatheter, setValue]);

  /**
   * Handles value change for incontinence types.
   */
  const handleValueChange = (
    index: number,
    field: 'day' | 'night',
    value: number
  ) => {
    // Get current form values
    const currentValues = [...(watch('incontinenceTypes') || [])];
    
    // Ensure the item exists
    if (!currentValues[index]) {
      currentValues[index] = {
        type: diapers[index].diaper.name,
        day: 0,
        night: 0,
      };
    }

    // Create a new object to avoid mutation
    const updatedItem = {
      ...currentValues[index],
      [field]: value
    };

    // Update the array with the new item
    currentValues[index] = updatedItem;

    // Update form state
    setValue('incontinenceTypes', currentValues, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  const saveDiaperUsage = async () => {
    try {
      // Create an array of promises for each diaper update
      const updatePromises = diapers.map(async (diaper, index) => {
        const currentType = incontinenceTypes[index];
        if (!currentType) return;

        // Update day usage
        if (currentType.day > 0) {
          await DiaperService.updateDiaperUsageAmount(
            residentId,
            diaper.diaper_id,
            currentType.day
          );
        }

        // Update night usage
        if (currentType.night > 0) {
          await DiaperService.updateDiaperUsageAmount(
            residentId,
            diaper.diaper_id,
            currentType.night
          );
        }
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      toast({
        title: t('diaperUsageUpdated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSaveSuccess?.();
    } catch (error) {
      console.error('Failed to update diaper usage:', error);
      toast({
        title: t('errorUpdatingDiaperUsage'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });

      onSaveError?.(error);
    }
  };

  // Expose the save function to the parent component
  useEffect(() => {
    if (window.incontinenceManagementSave) {
      window.incontinenceManagementSave = saveDiaperUsage;
    }
  }, [incontinenceTypes]);

  return (
    <Flex direction="column" alignItems="center">
      <Flex
        flexFlow="column"
        alignItems="flex-start"
        maxWidth="949px"
        w="100%"
        mb="55px"
      >
        <ResidentHeadline
          title={t('incontinenceManagement')}
          description={t('dailyNeedSenismartSubline').replace('{{name}}', residentName)}
        />
      </Flex>

      <Box maxWidth="500px" mb="25px">
        <Table variant="simple">
          <Thead backgroundColor="#F3F2F2">
            <Tr>
              <Th
                fontWeight={300}
                borderTopLeftRadius="20px"
                color="alea.500"
                fontSize="15px"
                height="50px"
                textTransform="inherit"
              >
                {t('seniSmartType')}
              </Th>
              <Th
                fontWeight={300}
                color="alea.500"
                fontSize="15px"
                height="50px"
                textTransform="inherit"
              >
                {t('daytime')}
              </Th>
              <Th
                fontWeight={300}
                borderTopRightRadius="20px"
                color="alea.500"
                fontSize="15px"
                height="50px"
                textTransform="inherit"
              >
                {t('nighttime')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {diapers.map((diaper, index) => (
              <Tr key={diaper.id} backgroundColor="white">
                <Td
                  height="50px"
                  py="0"
                  fontSize="18px"
                  fontWeight={
                    incontinenceTypes[index]?.day > 0 ||
                    incontinenceTypes[index]?.night > 0
                      ? '500'
                      : 'normal'
                  }
                >
                  {diaper.diaper.name}
                </Td>
                <Td height="50px" py="0">
                  <Select
                    value={incontinenceTypes[index]?.day || 0}
                    width="80px"
                    size="sm"
                    {...register(`incontinenceTypes.${index}.day`, {
                      onChange: (e) => handleValueChange(index, 'day', parseInt(e.target.value))
                    })}
                  >
                    {[...Array(11)].map((_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </Select>
                </Td>
                <Td height="50px" py="0">
                  <Select
                    value={incontinenceTypes[index]?.night || 0}
                    width="80px"
                    size="sm"
                    {...register(`incontinenceTypes.${index}.night`, {
                      onChange: (e) => handleValueChange(index, 'night', parseInt(e.target.value))
                    })}
                  >
                    {[...Array(11)].map((_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </Select>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Catheter checkbox at the bottom */}
      <Flex
        flexFlow="column"
        alignItems="flex-start"
        maxWidth="949px"
        w="100%"
        mb="25px"
      >
        <AleaCheckbox
          label={t('residentWearsCatheter', { name: residentName })}
          isChecked={typeof watchedHasCatheter === 'boolean' ? watchedHasCatheter : Boolean(initialHasCatheter)}
          onChange={(checked) => setValue('has_catheter', checked, { shouldDirty: true, shouldTouch: true })}
          register={register('has_catheter')}
          size="small"
        />
      </Flex>      

      {/* Validation text */}
      {showValidation && (
        <Flex
          flexFlow="column"
          alignItems="flex-start"
          maxWidth="949px"
          w="100%"
          mb="55px"
        >
          <Box textAlign="left">
            {!incontinenceTypes.some(type => type.day > 0 || type.night > 0) && (
              <Text 
                fontSize="15px" 
                color="#EF5C36" 
                fontWeight={300}
                lineHeight="normal"
                mb={1}
              >
                {t('dailyNeedsSeniSmartErrorMessage')}
              </Text>
            )}
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

// Add type declaration for the global window object
declare global {
  interface Window {
    incontinenceManagementSave?: () => Promise<void>;
  }
}

export default IncontinenceManagementTab;
